"use client";

import Footer from "@/components/footer/Footer";
import Header from "../header/Header";
import Navbar from "../header/Navbar";
import { ReactNode } from "react";
import { usePathname } from "next/navigation";
import { cn } from "@/utils/cn";

interface ScrollablePageLayoutProps {
  children: ReactNode;
}

/**
 * The comedian dashboard: `/<comedianSlug>/dashboard`, and only that. The venue
 * dashboard has three segments, so it never matches here.
 */
const COMEDIAN_DASHBOARD_ROUTE = /^\/[^/]+\/dashboard(\/|$)/;

/**
 * The venue admin: `/v/<venueSlug>/dashboard` and everything under it. The public
 * venue page `/v/<slug>` matches neither pattern and keeps the full site chrome.
 */
const VENUE_DASHBOARD_ROUTE = /^\/v\/[^/]+\/dashboard(\/|$)/;

/**
 * How much of the global nav a page keeps. The header is not part of this: it
 * renders on every page `Shell` serves. `Navbar` is ONE component worn two ways
 * (a bottom bar below `md`, the left rail above it), so these name the halves
 * that survive: `"mobile-bar-only"` is a breakpoint hide, `"none"` unmounts it.
 *
 * The footer rides this switch rather than a flag of its own. Every surface that
 * gives up part of the nav gives up the footer too, because the dashboard rail
 * carries both sets of destinations.
 *
 * The dashboard rail replaces the desktop nav on both dashboards. The comedian
 * dashboard also has a drawer to replace the bottom bar, so it uses `"none"`.
 * The venue dashboard has no drawer, so a phone keeps the bar as its way off the
 * dashboard.
 */
type GlobalNav = "full" | "mobile-bar-only" | "none";

export default function ScrollablePageLayout({
  children,
}: ScrollablePageLayoutProps) {
  const pathname = usePathname();

  if (
    pathname === "/purchase/success" ||
    pathname.endsWith("/purchase-success") ||
    // /show/[showId] is a pass-through ticket redirect — render it bare so
    // the visitor sees only its minimal spinner interstitial, not a flash of
    // app chrome
    pathname.startsWith("/show/")
  ) {
    return <>{children}</>;
  }

  if (pathname.startsWith("/purchase/mobile")) {
    // Don't render any layout for the mobile checkout page
    return (
      <div>
        <Header showMenus={false} />
        <div className="pt-[var(--header-height)] min-h-screen flex flex-col justify-between pb-[var(--navbar-height)] md:pb-0 md:pl-[var(--navbar-width)]">
          <main>{children}</main>
          <Footer />
        </div>
      </div>
    );
  }

  if (VENUE_DASHBOARD_ROUTE.test(pathname)) {
    return <Shell globalNav="mobile-bar-only">{children}</Shell>;
  }

  return COMEDIAN_DASHBOARD_ROUTE.test(pathname) ? (
    <Shell globalNav="none">{children}</Shell>
  ) : (
    <Shell>{children}</Shell>
  );
}

function Shell({
  children,
  globalNav = "full",
}: ScrollablePageLayoutProps & { globalNav?: GlobalNav }) {
  return (
    // overflow-x-CLIP, not -hidden: hidden forces overflow-y to `auto`, which
    // makes this div a scroll container and silently disables every
    // page-scroll position:sticky descendant (they pin to this
    // never-scrolling box instead of the page). clip prevents horizontal
    // overflow identically but creates no scroll container, so stickies work.
    <div className="overflow-x-clip">
      <Header />
      {globalNav !== "none" && (
        <div
          className={cn(
            // `lg`, not `md`: Navbar is the desktop left rail from `md` up, and
            // DashboardNavRail only arrives at `lg`. Hiding it at `md` leaves the
            // 768-1023px band with nothing leading back out of the dashboard.
            globalNav === "mobile-bar-only" && "lg:hidden"
          )}
        >
          <Navbar />
        </div>
      )}
      <div
        className={cn(
          "pt-[var(--header-height)] min-h-screen flex flex-col justify-between md:pb-0",
          // Each clearance padding goes with the half of the nav that survives.
          globalNav === "full" && "md:pl-[var(--navbar-width)]",
          // Navbar holds the left rail from `md` to `lg`, where the dashboard
          // rail's own spacer takes over the column.
          globalNav === "mobile-bar-only" &&
            "md:pl-[var(--navbar-width)] lg:pl-0",
          globalNav === "none" ? "pb-0" : "pb-[var(--navbar-height)]"
        )}
      >
        <main className="flex-1 flex flex-col">{children}</main>
        {globalNav === "full" && <Footer />}
      </div>
    </div>
  );
}
